import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

// Recommended products based on trade-in
const RECOMMENDED_PRODUCTS = [
  {
    id: 'nike-am90',
    name: 'Air Max 90',
    price: 130,
    image: 'https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/fd17b420-b388-4c8a-aaaa-e0a98ddf175f/air-max-90-shoes.png',
    description: 'Iconic cushioning, timeless style',
    tag: 'Recommended',
  },
  {
    id: 'nike-pegasus',
    name: 'Pegasus 41',
    price: 140,
    image: 'https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/c0e93f87-b5c1-42c0-bda4-f6c8ccb3af3f/pegasus-41-road-running-shoes.png',
    description: 'ReactX foam for responsive runs',
    tag: 'Best for Running',
  },
  {
    id: 'nike-tech-fleece',
    name: 'Tech Fleece Hoodie',
    price: 130,
    image: 'https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/tech-fleece-hoodie.png',
    description: 'Lightweight warmth, modern style',
    tag: 'Best Seller',
  },
];

const NikeOfferView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  // Calculate total credit from empties
  const totalCredit = state.empties.reduce((sum, item) => {
    const dollarValue = item.product.rewards?.dollarValue;
    if (dollarValue) {
      return sum + parseFloat(dollarValue.replace('$', ''));
    }
    return sum;
  }, 0);

  // Get products list from empties for display
  const products = state.empties.map(item => item.product);

  const handleSelectProduct = (productId: string) => {
    setSelectedProduct(productId);
  };

  const handleClaimOffer = () => {
    navigate('/nike/confirmation');
  };

  const handleJustCredit = () => {
    setSelectedProduct(null);
    navigate('/nike/confirmation');
  };

  const handleBack = () => {
    navigate('/nike/scan');
  };

  const handleImageError = (productId: string) => {
    setImageErrors(prev => new Set(prev).add(productId));
  };

  const selectedProductData = RECOMMENDED_PRODUCTS.find(p => p.id === selectedProduct);
  const afterCreditPrice = selectedProductData
    ? Math.max(0, selectedProductData.price - totalCredit)
    : 0;

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      {/* Header */}
      <header className="py-4 px-8 flex items-center justify-between border-b border-white/10">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-[#FA5400] font-bold uppercase tracking-widest text-sm">Nike Refurbished</span>
        </div>
        <div className="text-white font-bold">
          Your Credit: <span className="text-[#FA5400]">${totalCredit.toFixed(2)}</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-3">
            Nice! You've earned <span className="text-[#FA5400]">${totalCredit.toFixed(2)}</span>
          </h1>
          <p className="text-lg text-white/60">
            Use your credit toward something new, or save it for later.
          </p>
        </motion.div>

        {/* Product Recommendations */}
        <div className="grid grid-cols-3 gap-6 max-w-5xl w-full mb-8">
          {RECOMMENDED_PRODUCTS.map((product, index) => {
            const priceAfterCredit = Math.max(0, product.price - totalCredit);
            const hasError = imageErrors.has(product.id);

            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                onClick={() => handleSelectProduct(product.id)}
                className={`
                  relative rounded-2xl p-6 cursor-pointer transition-all duration-300 border-2
                  ${selectedProduct === product.id
                    ? 'bg-white border-[#FA5400]'
                    : 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/20'}
                `}
              >
                {/* Tag */}
                <span className={`absolute top-4 right-4 text-xs px-3 py-1 rounded-full font-medium
                  ${selectedProduct === product.id ? 'bg-[#FA5400] text-white' : 'bg-white/10 text-white/60'}`}>
                  {product.tag}
                </span>

                {/* Product Image */}
                <div className={`w-full aspect-square rounded-xl mb-4 flex items-center justify-center overflow-hidden
                  ${selectedProduct === product.id ? 'bg-gray-100' : 'bg-white/5'}`}>
                  {!hasError ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain p-4"
                      onError={() => handleImageError(product.id)}
                    />
                  ) : (
                    <span className={`text-sm text-center px-2 ${selectedProduct === product.id ? 'text-gray-400' : 'text-white/40'}`}>
                      {product.name}
                    </span>
                  )}
                </div>

                {/* Product Info */}
                <h3 className={`font-bold text-lg mb-1 ${selectedProduct === product.id ? 'text-[#111111]' : 'text-white'}`}>
                  {product.name}
                </h3>
                <p className={`text-sm mb-3 ${selectedProduct === product.id ? 'text-gray-500' : 'text-white/60'}`}>
                  {product.description}
                </p>

                {/* Pricing */}
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${selectedProduct === product.id ? 'text-[#111111]' : 'text-white'}`}>
                    ${priceAfterCredit.toFixed(2)}
                  </span>
                  {priceAfterCredit < product.price && (
                    <span className={`text-sm line-through ${selectedProduct === product.id ? 'text-gray-400' : 'text-white/40'}`}>
                      ${product.price}
                    </span>
                  )}
                </div>

                {/* Selection indicator */}
                {selectedProduct === product.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-[#FA5400] rounded-full flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col items-center gap-4">
          {selectedProduct && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={handleClaimOffer}
              className="px-12 py-4 bg-[#FA5400] text-white rounded-full font-bold uppercase tracking-wider text-lg hover:bg-[#E04E00] transition-colors"
            >
              Claim {selectedProductData?.name} for ${afterCreditPrice.toFixed(2)}
            </motion.button>
          )}

          <button
            onClick={handleJustCredit}
            className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
          >
            No Thanks, Just Give Me My ${totalCredit.toFixed(2)} Credit
          </button>
        </div>

        {/* Items Traded Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-white/40 text-sm">
            {products.length} item{products.length !== 1 ? 's' : ''} traded in
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default NikeOfferView;
